/**
 * Actor tax profile + 1099 client API.
 *
 * Mirrors the schemas in `apps/api/src/routes/v1/account-tax.ts`. Encrypted
 * fields (SSN / EIN) are NEVER returned by GET — instead the payload carries
 * `has_*` booleans and `*_last4` plaintext suitable for masked display.
 *
 * The form-upload flow is three steps because R2 sits behind a presigned URL:
 *
 *   1. POST /tax-profile/upload-form  → { upload_url, r2_key }
 *   2. PUT  upload_url (raw PDF bytes)
 *   3. POST /tax-profile/finalize-form { form_type, r2_key }
 *
 * `useUploadTaxForm` packages all three so callers see a single mutation.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'

export type TaxFormType = 'pending' | 'w9' | 'w8ben' | 'w8ben_e'

export interface TaxProfile {
  user_id: string
  form_type: TaxFormType
  form_uploaded_at: string | null
  form_sha256: string | null
  has_ssn: boolean
  ssn_last4: string | null
  has_ein: boolean
  ein_last4: string | null
  loan_out_name: string | null
  has_loan_out_ein: boolean
  loan_out_ein_last4: string | null
  loan_out_address_line1: string | null
  loan_out_address_line2: string | null
  loan_out_city: string | null
  loan_out_state: string | null
  loan_out_postal: string | null
  loan_out_country: string | null
  payee_legal_name: string | null
  payee_address_line1: string | null
  payee_address_line2: string | null
  payee_city: string | null
  payee_state: string | null
  payee_postal: string | null
  payee_country: string | null
  phone: string | null
  alternate_email: string | null
  backup_withholding: boolean
  foreign_country_of_residence: string | null
  foreign_treaty_claim: string | null
  signed_at: string | null
  last_updated_at: string
}

export type TaxProfileUpdate = Partial<{
  form_type: Exclude<TaxFormType, 'pending'>
  loan_out_name: string | null
  loan_out_address_line1: string | null
  loan_out_address_line2: string | null
  loan_out_city: string | null
  loan_out_state: string | null
  loan_out_postal: string | null
  loan_out_country: string | null
  payee_legal_name: string | null
  payee_address_line1: string | null
  payee_address_line2: string | null
  payee_city: string | null
  payee_state: string | null
  payee_postal: string | null
  payee_country: string | null
  phone: string | null
  alternate_email: string | null
  backup_withholding: boolean
  foreign_country_of_residence: string | null
  foreign_treaty_claim: string | null
  /** Typed full-name signature; required when ESIGN-certifying the form. */
  signature_data: string
}>

export interface SsnSubmission {
  ssn: string
  /** Typed legal name; must match `payee_legal_name` if one is on file. */
  signature: string
}

export interface OneOhNineNineRecord {
  tax_year: number
  total_paid_cents: number
  form_generated_at: string | null
  irs_filed: boolean
  state_filed: boolean
  download_url: string | null
  download_expires_at: string | null
}

const taxProfileKey = ['account', 'tax-profile'] as const
const taxFormDownloadKey = ['account', 'tax-form-download'] as const

export function useTaxProfile() {
  return useQuery({
    queryKey: taxProfileKey,
    queryFn: () => api<TaxProfile | null>('/api/v1/account/tax-profile'),
  })
}

export function useUpdateTaxProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: TaxProfileUpdate) =>
      api<TaxProfile>('/api/v1/account/tax-profile', {
        method: 'PUT',
        body: JSON.stringify(patch),
      }),
    onSuccess: (next) => {
      qc.setQueryData<TaxProfile>(taxProfileKey, next)
    },
  })
}

export function useUpdateSsn() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SsnSubmission) =>
      api<{ ok: true; ssn_last4: string }>('/api/v1/account/tax-profile/ssn', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taxProfileKey })
    },
  })
}

export function useUpdateEin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ein: string) =>
      api<{ ok: true; ein_last4: string }>('/api/v1/account/tax-profile/ein', {
        method: 'POST',
        body: JSON.stringify({ ein }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taxProfileKey })
    },
  })
}

export function useUpdateLoanOutEin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ein: string) =>
      api<{ ok: true; ein_last4: string }>('/api/v1/account/tax-profile/loan-out-ein', {
        method: 'POST',
        body: JSON.stringify({ ein }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taxProfileKey })
    },
  })
}

/**
 * Upload a W-9 / W-8BEN / W-8BEN-E PDF in three steps:
 *   1. presign → r2_key + upload_url
 *   2. PUT pdf to upload_url (no auth header — R2 reads the signature)
 *   3. finalize-form → record key + sha256
 *
 * The mutation surfaces a single `mutate({ formType, file })` so the UI
 * doesn't need to know about the multi-step coordination.
 */
export function useUploadTaxForm() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      formType,
      file,
    }: {
      formType: Exclude<TaxFormType, 'pending'>
      file: File
    }) => {
      if (file.type !== 'application/pdf') {
        throw new Error('Tax form must be a PDF.')
      }
      if (file.size < 1024 || file.size > 5 * 1024 * 1024) {
        throw new Error('Tax form must be between 1 KB and 5 MB.')
      }
      const presigned = await api<{ upload_url: string; r2_key: string; expires_at: string }>(
        '/api/v1/account/tax-profile/upload-form',
        {
          method: 'POST',
          body: JSON.stringify({ form_type: formType, content_type: 'application/pdf' }),
        },
      )
      const putRes = await fetch(presigned.upload_url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': 'application/pdf' },
      })
      if (!putRes.ok) throw new Error(`Upload failed: ${putRes.status} ${putRes.statusText}`)

      return api<{ ok: true; form_uploaded_at: string; sha256: string }>(
        '/api/v1/account/tax-profile/finalize-form',
        {
          method: 'POST',
          body: JSON.stringify({ form_type: formType, r2_key: presigned.r2_key }),
        },
      )
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taxProfileKey })
      void qc.invalidateQueries({ queryKey: taxFormDownloadKey })
    },
  })
}

export function useTaxFormDownloadUrl(enabled = true) {
  return useQuery({
    queryKey: taxFormDownloadKey,
    queryFn: () =>
      api<{ download_url: string; expires_at: string }>(
        '/api/v1/account/tax-profile/form-download-url',
      ),
    enabled,
    // Server returns a 5-min URL; refresh proactively at 4 minutes so a
    // tab left open doesn't try to download with an expired signature.
    staleTime: 4 * 60 * 1000,
    gcTime: 4 * 60 * 1000,
  })
}

export function use1099Record(year: number, enabled = true) {
  return useQuery({
    queryKey: ['account', '1099', year],
    queryFn: () => api<OneOhNineNineRecord>(`/api/v1/account/1099/${year}`),
    enabled,
  })
}
